import * as Sentry from '@sentry/react';
import { Alert, Button, Divider, Snackbar, Stack, Tooltip, useMediaQuery, FormControl, Select, MenuItem } from '@mui/material'
import React from 'react'
import { Lineup, LineupRubric } from './store/types'
import { useDispatch, useSelector } from 'react-redux'
import { ArrowBack } from '@material-ui/icons'
import { addPlayer, resetLineup, setLineup, setLineupChecked, setSelectedLineup, setSimResults, setProjectionsRedux, setLineupRandomness, setDefaultRandomness } from './store/actions'
import { notOnSlateMessage, playersNotOnSlate } from './helpers/slatePlayerCheck'
import LineupTable from './LineupTable'
import MobileLineup from './MobileLIneup'
import Lottie from 'react-lottie';
import { IRootState } from '../../store/reducer'
import { ArrowForward, Error, Refresh } from '@mui/icons-material'
import { useUserId } from "../utils/authentication";
import { useGetLambdaStatusForContest } from '../preContest/queries/settingsQueries'
import LineupNameEditor from './LineupNameEditor';
import { animationData } from './completionAnimation';

const RANDOMNESS_OPTIONS = [0, 5, 10, 15, 20, 25, 30];

const actionButtonSx = {
  borderRadius: '6px',
  height: '36px',
  color: 'rgba(255, 255, 255, 1)',
  padding: '8px 18px',
  fontWeight: 500,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
} as const;

const randomnessSelectSx = {
  backgroundColor: 'transparent',
  color: 'white',
  borderRadius: '6px',
  border: 'none !important',
  fontSize: '14px',
  height: '36px',
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-icon': { color: 'white' },
} as const;

const SingleLineup = ({ lineup }: { lineup: Lineup }) => {
  const { selectedSlate, selectedSite, selectedSport } = useSelector((state: IRootState) => state.common);
  const { projections, slateProjections, percentToFirst, poolSize, selectedLineupId, defaultRandomness} = useSelector((state: IRootState) => state.lineupBuilder);
  const userId = useUserId();
  const [completeThisLineupErrorMessage, setCompleteThisLineupErrorMessage] = React.useState('');
  const [completeThisLineupLoading, setCompleteThisLineupLoading] = React.useState(false);
  const [lastCompletedRandomness, setLastCompletedRandomness] = React.useState<number | undefined>(undefined);
  const [selectOpen, setSelectOpen] = React.useState(false);
  const auth = 'awe' + userId;
  const dispatch = useDispatch();
  const matches = useMediaQuery('(min-width:1199px)');
  const salaryRemaining = selectedSlate?.lineupRules?.teamSalary - lineup?.players?.reduce((partialSum, a) => partialSum + a.salary, 0);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  const { data: progressData } = useGetLambdaStatusForContest(
    selectedSlate?.slateId,
    auth,
    lineup.simStatus === 'loading' ? false : true
  );

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: animationData,
    renderer: 'svg'
  }

  const TotalSumOfDataRow = () => (
    <Stack direction='row' justifyContent='space-between' width='100%' sx={{
      '& div': {
        padding: '8px',
      },
      backgroundColor: 'rgba(161, 0, 18, 0.15)',
    }}>
      <Stack>Totals:</Stack>
      <Stack>Proj: {lineup.players?.reduce((a, b) => a + b.fpp, 0).toFixed(2)}</Stack>
      <Stack>Own: {(lineup.players?.reduce((a, b) => {
        const own = ['MVP - 2X Points', 'MVP - 1.5X Points', 'MVP - 1.5x Points', 'CPT'].includes(b.position) ? b.cptOwnership
                  : b.position === 'STAR - 1.5X Points' ? b.starOwnership
                  : b.position === 'PRO - 1.2X Points' ? b.proOwnership
                  : b.ownership;
        return a + own;
      }, 0) * 100).toFixed(2)}</Stack>
    </Stack>
  )

  const completeLineup = async () => {
    setCompleteThisLineupLoading(true);
    setCompleteThisLineupErrorMessage('');
    setLastCompletedRandomness(lineup.randomness);
    
    // Check for players in lineup that aren't in projections and add them
    const updatedProjections = [...projections];
    lineup.players.forEach((player: LineupRubric) => {
      if (player.name && player.nameAndId) {
        // Check if this player exists in projections
        const existsInProjections = projections.some((p: any) => p.nameAndId === player.nameAndId);
        
        if (!existsInProjections) {
          // Create a projection object for this player
          const newProjection = {
            nameAndId: player.nameAndId,
            name: player.name,
            team: player.team,
            teamLogo: player.teamLogo || '',
            opponent: player.opponent,
            opponentLogo: player.opponentLogo || '',
            position: player.position,
            salary: player.salary,
            projection: player.fpp, // Use fpp as projection
            fpp: player.fpp,
            ownership: player.ownership,
            cptOwnership: player.cptOwnership || 0,
            starOwnership: player.starOwnership || 0,
            proOwnership: player.proOwnership || 0,
            id: player.id,
            gameTime: player.gameTime,
            confirmedLineup: player.confirmedLineup,
            lineupPosition: player.lineupPosition,
            canBeAddedToPositions: player.canBeAddedToPositions,
          };
          updatedProjections.push(newProjection);
        }
      }
    });
    
    const request = {
      user: auth,
      slateId: selectedSlate.slateId,
      projections: updatedProjections, // Use updated projections that include lineup players
      // userLineup: [lineup.players.map((x: LineupRubric) => (x .nameAndId &&  x.nameAndId)).join(',')]
      userLineup: lineup.players.map((x: LineupRubric) => (x.nameAndId)).join(','),
      randomness: (lineup.randomness !== undefined ? lineup.randomness : 0) / 100, // Convert percentage to decimal (0% -> 0.00)
    }

    const compLineup = await fetch(`${process.env.REACT_APP_API_URL}/api/contestGenerator/optimizeLineup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request)
    }).then((res) => res.json());

    if(compLineup.solveFailed) {
      setCompleteThisLineupErrorMessage(compLineup.errorMessage)
      Sentry.captureMessage('Complete my lineup failed');
    }

    if(compLineup.lineup.length) {
      // Update Redux with the new projections if any were added
      if (updatedProjections.length > projections.length) {
        dispatch(setProjectionsRedux(updatedProjections));
      }
      
      const players = compLineup.lineup.split(',').map((x: any) => updatedProjections.find((y: any) => y.nameAndId === x))
      const playersInTheirPosition = lineup.players.map((x: LineupRubric, index: number) => {
        const player = players[index];
        const newPlayer =  {
          ...player,
          position: x.position,
          isLocked: x.isLocked || false, // Preserve lock state
        }
        return newPlayer;
      })
      dispatch(setLineup(playersInTheirPosition, selectedLineupId))
    }
    setCompleteThisLineupLoading(false);
  }


  const startSimulation = async () => {
    // A lineup carried over from another slate keeps that slate's ids; the sim would reject them. Checked against
    // the whole slate, since `projections` only holds the players the matchup filter shows.
    const offSlate = playersNotOnSlate(lineup.players, slateProjections);
    if (offSlate.length > 0) {
      dispatch(setLineupChecked(lineup.id, { isValid: false, errorMessage: notOnSlateMessage(offSlate) }));
      return;
    }
    const request = {
      user: auth,
      slateId: selectedSlate.slateId,
      contestType: "PreContest",
      uploadData: true,
      totalEntries: poolSize,
      percentToFirst: percentToFirst,
      projections: projections,
      simulationCount: 0,
      userLineup: lineup.players.map((x: LineupRubric) => (x.nameAndId)).join(','),
    }
    await fetch(`${process.env.REACT_APP_API_URL}/api/contests/simulateLambdaContest`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request)
    }).then(() => {
    });
    dispatch(setSimResults({
      lineupId: lineup.id,
      simStatus: 'loading',
      simResults: []
    }))
  }


  const goBack = () => {
    dispatch(setSelectedLineup(null))
  }

  React.useEffect(() => {
    if (salaryRemaining >= 0 && lineup.players?.length > 0 && lineup.players.filter(x => x.name === '')?.length === 0 && !lineup.lineupChecked) {
      const checkLineup = async () => {
        const request = {
          site: selectedSite.site,
          sport: selectedSport.sport,
          type: selectedSlate.type,
          players: lineup.players.map((x: LineupRubric) => ({
            PlayerName: x.name,
            Position: x.position,
            Team: x.team,
            Opponent: x.opponent,
          }))
        };

        const response: { isValid: boolean, errorMessage: string } = await fetch(`${process.env.REACT_APP_API_URL}/api/contests/validateLineup`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request)
        }).then(res => res.json()).catch(err => {
          Sentry.captureException(err);
          console.log(err)
        });

        if (!lineup.lineupChecked && lineup.lineupErrors.length === 0) {
          dispatch(setLineupChecked(lineup.id, { isValid: response?.isValid, errorMessage: response?.errorMessage }))
        }
      };
      checkLineup();
    }
  }, [lineup])


  React.useEffect(() => {
    const getResults = async () => {
      const params = {
        slateId: selectedSlate?.slateId,
        user: auth,
        contestType: 'PreContest',
        includeAllLineups: 'true',
      }

      const searchParams = new URLSearchParams(params).toString();
      const results = await fetch(`${process.env.REACT_APP_API_URL}/api/contests/getRoiByLineup?${searchParams}`).then(res => res.json());
      if (results.length > 0) {
        const result = results[0];
        dispatch(setSimResults({
          lineupId: lineup.id,
          simStatus: 'complete',
          simResults: {
            simRoi: result.simLineupRoiPct,
            cashPct: result.cashPct,
            topTenPct: result.top10Pct,
            winPct: result.winPct,
            roiContext: result.roiContext,
          }
        }))
      } else {
        dispatch(setSimResults({
          lineupId: lineup.id,
          simStatus: 'complete',
          simResults: {}
        }))
      }
    }

    if (progressData?.progress === 1) {
      getResults();
    } else if (progressData?.progress === -1) {
      dispatch(setSimResults({
        lineupId: lineup.id,
        simStatus: 'error',
        simResults: []
      }))
    }

  }, [selectedSite, selectedSlate, progressData])

  // Shared between the desktop single-row and mobile two-row layouts below.
  const buttonFontSize = matches ? '14px' : '10px';

  const clearLineupButton = lineup.players?.filter(x => x.name !== '').length > 0 ? (
    <Tooltip title="Removes all players except those you've locked.">
      <span>
        <Button onClick={() => dispatch(resetLineup(lineup.id))} endIcon={<Refresh />} sx={{
          ...actionButtonSx,
          fontSize: buttonFontSize,
          '&:hover': { backgroundColor: 'red', color: 'rgba(255, 255, 255, .7)' },
        }}>Clear Lineup</Button>
      </span>
    </Tooltip>
  ) : null;

  const randomnessSelect = (
    <div onMouseLeave={() => setSelectOpen(false)}>
      <Tooltip title={selectOpen ? "" : "Adds randomness to projections when completing lineups to increase diversity."}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={lineup.randomness !== undefined ? lineup.randomness : defaultRandomness}
            onChange={(e) => {
              const newRandomness = e.target.value as number;
              dispatch(setLineupRandomness(lineup.id, newRandomness));
              dispatch(setDefaultRandomness(newRandomness));
            }}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            sx={randomnessSelectSx}
            displayEmpty
            renderValue={(value) => `${value}% Randomness`}
          >
            {RANDOMNESS_OPTIONS.map(v => <MenuItem key={v} value={v}>{v}% Randomness</MenuItem>)}
          </Select>
        </FormControl>
      </Tooltip>
    </div>
  );

  const completeLineupButton = ['NBA', 'MLB', 'PGA', 'MMA', 'NASCAR', 'CFB', 'NFL'].indexOf(selectedSport.sport.toUpperCase()) >= 0 ? (
    <Tooltip title="Auto-completes your lineup with the best available players from your filtered pool.">
      <span>
        <Button onClick={completeLineup} variant='outlined' disabled={
          lineup?.players?.filter(x => x.name === '').length === 0 &&
          lineup.randomness === lastCompletedRandomness
        } sx={{
          ...actionButtonSx,
          fontSize: buttonFontSize,
          '&:hover': { color: 'rgba(255, 255, 255, .7)' },
        }}>Complete this Lineup</Button>
      </span>
    </Tooltip>
  ) : null;

  const runSimulationButton = (
    <Button onClick={startSimulation} endIcon={<ArrowForward />} disabled={!lineup.lineupChecked || lineup.simStatus === 'complete' || lineup.simStatus === 'loading'} sx={{
      ...actionButtonSx,
      fontSize: buttonFontSize,
      backgroundColor: '#015ACE',
      '&:hover': { backgroundColor: '#015ACE', color: 'rgba(255, 255, 255, .7)' },
    }}>Run Lineup Simulation</Button>
  );

  return (
    <Stack borderRadius={{ xs: '0', lg: '10px' }} fontFamily='Inter' width="100%" position='relative' height={{ lg: 'calc(100vh - 350px)' }} sx={{ flex: { xs: 1 }, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Snackbar open={completeThisLineupErrorMessage !== ''} autoHideDuration={6000} onClose={() => setCompleteThisLineupErrorMessage('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert variant='filled' severity="error" sx={{ width: '100%' }}>{completeThisLineupErrorMessage}</Alert>
      </Snackbar>
      <Stack padding='15px 12px' direction='row' alignItems='center' justifyContent='space-between' columnGap='15px' width='100%' boxSizing='border-box' flexShrink={0}>
        <Stack fontSize={{ xs: '16px', lg: '24px' }} fontWeight={700} direction='row' alignItems='center' columnGap={'15px'} flexGrow={1} overflow='hidden' maxWidth='100%'>
          <Stack sx={{ cursor: 'pointer' }}>
            <ArrowBack  onClick={goBack} />
          </Stack>
          <LineupNameEditor sportsbook={selectedSlate?.site} slateId={selectedSlate?.slateId} lineupId={lineup?.id} />
          {
            lineup.lineupChecked && (
              <Stack justifyContent='center' flexShrink={0} sx={{ display: { xs: 'none', lg: 'flex' } }}>
                <Lottie
                  options={defaultOptions}
                  height={40}
                  width={40}
                />
              </Stack>
            )
          }
          {
            lineup.lineupErrors?.length > 0 && (
              <Tooltip title={lineup.lineupErrors[0]} placement='top' sx={{ color: 'red' }}>
                <Error />
              </Tooltip>
            )
          }
        </Stack>
        <Stack direction='row' columnGap='15px'>
          <Stack direction={{ xs: 'column', lg: 'row' }} fontSize={{ xs: '12px', lg: '14px' }} alignItems='center' color='rgba(255, 255, 255, 0.70)'>Avg.Rem/Player: <Stack paddingTop={{ xs: '4px', lg: '0px'}} component='span' fontSize='18px' fontWeight={500} color='rgba(255, 255, 255, 1)'>{salaryRemaining > 0 ? formatter.format(Math.round(salaryRemaining / (lineup.players.filter(x => x.name === '').length || 1))) : 0}</Stack></Stack>
          <Divider orientation='vertical' flexItem sx={{ display: { md: 'none', lg: 'block' }, borderColor: 'rgba(255, 255, 255, 0.30)' }} />
          <Stack direction={{ xs: 'column', lg: 'row' }} fontSize={{ xs: '12px', lg: '14px' }} alignItems='center' color='rgba(255, 255, 255, 0.70)'>
            {
              salaryRemaining < 0 && (
                <Tooltip title='' placement='top' sx={{ color: 'red' }}>
                  <Error />
                </Tooltip>
              )
            }
            Rem. Salary: <Stack paddingTop={{ xs: '4px', lg: '0px'}} component='span' fontSize='18px' fontWeight={500} color='rgba(255, 255, 255, 1)'>{formatter.format(salaryRemaining)}</Stack>
          </Stack>
        </Stack>
      </Stack>
      {
        !matches ? (
          <Stack width='100%' sx={{ flex: 1, overflow: 'auto', display: { lg: 'none', xs: 'flex' }, flexDirection: 'column', minHeight: 0 }}>
            <MobileLineup lineup={lineup}/>
          </Stack>
        ) : (
          <Stack flexGrow={1} overflow='scroll' sx={{
            '&::-webkit-scrollbar': {
              display: 'none'
            },
           'MsOverflowStyle': 'none',
            'scrollbarWidth': 'none',
          }}>
          {
            lineup.players?.length > 0 && (
              <LineupTable loading={completeThisLineupLoading} players={lineup.players} />
            )
          }
        </Stack>
        )
      }
      <Stack direction='column' width='99%' flexShrink={0} pb='24px' sx={{
        "& .Mui-disabled": {
          backgroundColor: 'rgba(255, 255, 255, .15)',
        },
      }}>
        <TotalSumOfDataRow />
        {matches ? (
        /* Desktop: single row with wrap */
        <Stack direction='row' justifyContent='flex-end' columnGap='8px' alignItems='center' flexWrap='wrap' rowGap='4px'>
          {clearLineupButton}
          {randomnessSelect}
          {completeLineupButton}
          {runSimulationButton}
        </Stack>
        ) : (
        /* Mobile: two distinct rows */
        <Stack sx={{ display: 'flex', gap: '4px' }}>
          <Stack direction='row' justifyContent='flex-end' columnGap='4px' alignItems='center'>
            {clearLineupButton}
            {randomnessSelect}
          </Stack>
          <Stack direction='row' justifyContent='flex-end' columnGap='8px' alignItems='center'>
            {completeLineupButton}
            {runSimulationButton}
          </Stack>
        </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default SingleLineup